import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaUserGraduate, FaUndo, FaIdCard, FaArrowRight } from 'react-icons/fa';
import './FindAlumni.css';

const FindAlumni = () => {
  const navigate = useNavigate();
  const mainColor = '#006186';

  const [formData, setFormData] = useState({
    fio: '',
    direction: 'не выбрано',
    specialty: 'не выбрано',
    graduation_year: '',
    performance_year: ''
  });

  const [directions, setDirections] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [alumniData, setAlumniData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  // Загрузка направлений
  useEffect(() => {
    const fetchDirections = async () => {
      try {
        const response = await fetch('/api/directions');
        const data = await response.json();
        setDirections(data);
      } catch (err) {
        setError('Не удалось загрузить направления');
      }
    };
    fetchDirections();
  }, []);

  // Загрузка специальностей при выборе направления
  useEffect(() => {
    const fetchSpecialties = async () => {
      if (formData.direction && formData.direction !== 'не выбрано') {
        try {
          const response = await fetch(`/api/specialties/${encodeURIComponent(formData.direction)}`);
          const data = await response.json();
          setSpecialties(data);
          setFormData(prev => ({ ...prev, specialty: 'не выбрано' }));
        } catch (err) { 
          setError('Не удалось загрузить специальности'); 
        }
      } else { 
        setSpecialties([]); 
      }
    };
    fetchSpecialties();
  }, [formData.direction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Функция для получения фото
  const getImageUrl = (alumni) => {
    if (alumni.image_url) {
      // Если это Data URL (начинается с data:image)
      if (alumni.image_url.startsWith('data:image')) {
        return alumni.image_url;
      }
      // Если это относительный путь
      if (alumni.image_url.startsWith('/uploads')) {
        return `http://192.168.0.102:5000${alumni.image_url}`;
      }
      // Если это внешняя ссылка
      return alumni.image_url;
    }
    return null; // нет фото
  };

  const groupSimilarAlumni = (data) => {
    const grouped = {};
    data.forEach(alumni => {
      const key = `${alumni.fio}_${alumni.specialty}_${alumni.graduation_year}`;
      if (!grouped[key]) {
        grouped[key] = { 
          ...alumni, 
          count: 1, 
          performance_years: [alumni.performance_year], 
          ids: [alumni.id] 
        };
      } else {
        grouped[key].count += 1;
        if (!grouped[key].performance_years.includes(alumni.performance_year)) {
          grouped[key].performance_years.push(alumni.performance_year);
        }
        grouped[key].ids.push(alumni.id);
      }
    });
    return Object.values(grouped);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    try {
      const response = await fetch('/api/alumni', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, fio: formData.fio.trim() })
      });
      if (!response.ok) throw new Error('Ошибка сервера');
      const data = await response.json();
      const groupedData = groupSimilarAlumni(data);
      setAlumniData(groupedData);
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="news-page bg-white">
      <section className="news-hero py-5">
        <Container>
          <h1 className="display-4 fw-bold mb-3" style={{ color: '#006186' }}>Поиск выпускников</h1>
          <div className="header-line mb-4" style={{ backgroundColor: mainColor }}></div>
        </Container>
      </section>

      <Container className="py-5">
        {/* ФОРМА ПОИСКА */}
        <Card className="border-0 shadow-sm p-4 mb-5 rounded-4 bg-light-subtle">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12} className="mb-4">
                <Form.Group>
                  <Form.Label className="fw-bold text-dark">Фамилия и Имя</Form.Label>
                  <Form.Control
                    type="text" name="fio" value={formData.fio} onChange={handleChange}
                    placeholder="Напр: Асанов Асан" className="py-2 border-0 shadow-sm"
                  />
                </Form.Group>
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-bold text-dark">Год вступления</Form.Label>
                <Form.Control
                  type="number" name="performance_year" value={formData.performance_year} onChange={handleChange}
                  placeholder="2016" className="py-2 border-0 shadow-sm"
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-bold text-dark">Год окончания</Form.Label>
                <Form.Control
                  type="number" name="graduation_year" value={formData.graduation_year} onChange={handleChange}
                  placeholder="2020" className="py-2 border-0 shadow-sm"
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-bold text-dark">Направление</Form.Label>
                <Form.Select
                  name="direction" value={formData.direction} onChange={handleChange}
                  className="py-2 border-0 shadow-sm"
                >
                  <option value="не выбрано">Выберите...</option>
                  {directions.map((dir, i) => <option key={i} value={dir}>{dir}</option>)}
                </Form.Select>
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label className="fw-bold text-dark">Специальность</Form.Label>
                <Form.Select
                  name="specialty" value={formData.specialty} onChange={handleChange}
                  disabled={formData.direction === 'не выбрано'} className="py-2 border-0 shadow-sm"
                >
                  <option value="не выбрано">Выберите...</option>
                  {specialties.map((spec, i) => <option key={i} value={spec}>{spec}</option>)}
                </Form.Select>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Button type="submit" className="px-4 border-0 d-flex align-items-center" style={{ backgroundColor: mainColor }}>
                <FaSearch className="me-2" /> {loading ? 'Поиск...' : 'Найти'}
              </Button>
              <Button variant="outline-secondary" onClick={() => setSearchPerformed(false)} className="px-4">
                <FaUndo className="me-2" /> Сброс
              </Button>
            </div>
          </Form>
        </Card>

        {/* РЕЗУЛЬТАТЫ ПОИСКА */}
        <AnimatePresence>
          {searchPerformed && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold m-0 text-dark">Результаты поиска</h4>
                <Badge style={{ backgroundColor: mainColor }} className="p-2 px-3 fs-6">
                  Найдено: {alumniData.length}
                </Badge>
              </div>

              {error && <Alert variant="danger" className="rounded-3 border-0 shadow-sm">{error}</Alert>}

              <Row className="g-3">
                {alumniData.map((alumni) => {
                  const imageUrl = getImageUrl(alumni);
                  
                  return (
                    <Col lg={4} md={6} sm={12} key={alumni.ids[0]}>
                      <motion.div
                        whileHover={{ y: -5 }}
                        className="alumni-compact-card shadow-sm h-100"
                        onClick={() => navigate('/alumni-details', { state: { alumni } })}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-top-accent" style={{ backgroundColor: mainColor }}></div>
                        <div className="p-3 text-center">
                          <div className="avatar-container mb-3">
                            {imageUrl ? (
                              <img 
                                src={imageUrl} 
                                alt={alumni.fio} 
                                className="avatar-img"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div class="avatar-placeholder-vk" style="width:80px; height:80px; border-radius:50%; background: linear-gradient(145deg, #006186, #00a1d9); display:flex; align-items:center; justify-content:center; margin:0 auto;">
                                      <svg stroke="currentColor" fill="white" stroke-width="0" viewBox="0 0 448 512" height="25" width="25" xmlns="http://www.w3.org/2000/svg"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div className="avatar-placeholder-vk" style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                background: 'linear-gradient(145deg, #006186, #00a1d9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto'
                              }}>
                                <FaUserGraduate size={25} color="#fff" />
                              </div>
                            )}
                          </div>

                          <h6 className="fw-bold text-dark mb-1 text-truncate" title={alumni.fio}>
                            {alumni.fio}
                          </h6>

                          <div className="text-muted small mb-3" style={{ height: '40px', overflow: 'hidden' }}>
                            {alumni.specialty}
                          </div>

                          <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                            <span className="badge-year" style={{ background: '#f0f0f0', padding: '3px 10px', borderRadius: '20px' }}>
                              {alumni.graduation_year || 'н.в.'}
                            </span>
                            <Button variant="link" className="p-0 text-decoration-none fw-bold btn-sm" style={{ color: mainColor }}>
                              Профиль <FaArrowRight size={12} />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    </Col>
                  );
                })}
              </Row>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </div>
  );
};

export default FindAlumni;